import { motion, animate } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Zap, Shield, Clock, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState, useCallback } from "react";
import { usePlatformConfig } from "@/hooks/usePlatformConfig";

const Counter = ({ target, suffix = "" }: { target: number; suffix?: string }) => {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const controls = animate(0, target, {
      duration: 2,
      ease: "easeOut",
      onUpdate: (v) => setValue(Math.floor(v)),
    });
    return () => controls.stop();
  }, [target]);

  return <span ref={ref}>{value.toLocaleString("pt-BR")}{suffix}</span>;
};

const HeroSection = () => {
  const { config } = usePlatformConfig();
  const slides = config.hero_slides.filter(s => s.is_active);
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => setCurrent(c => (c + 1) % slides.length), [slides.length]);
  const prev = useCallback(() => setCurrent(c => (c - 1 + slides.length) % slides.length), [slides.length]);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next, slides.length]);

  const slide = slides[current] || slides[0];
  if (!slide) return null;

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-background">
      {/* Background pattern */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--foreground)) 1px, transparent 0)`,
          backgroundSize: '32px 32px'
        }} />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, hsl(0 100% 50% / 0.3), transparent 70%)' }} />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-5"
          style={{ background: 'radial-gradient(circle, hsl(0 100% 50% / 0.4), transparent 70%)' }} />
      </div>

      <div className="container relative z-10 pt-24 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left column - Text */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6">
                <Zap className="h-4 w-4" />
                {config.hero_badge_text}
              </span>
            </motion.div>

            <motion.h1
              key={`title-${slide.id}`}
              className="text-4xl sm:text-5xl md:text-6xl font-bold leading-[1.08] tracking-tight mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              {slide.title.includes("restaurante") ? (
                <>
                  {slide.title.split("restaurante")[0]}
                  <span className="text-gradient">restaurante</span>
                  {slide.title.split("restaurante")[1]}
                </>
              ) : (
                <span dangerouslySetInnerHTML={{ __html: slide.title.replace(/\*\*(.*?)\*\*/g, '<span class="text-gradient">$1</span>') }} />
              )}
            </motion.h1>

            <motion.p
              key={`sub-${slide.id}`}
              className="text-lg text-muted-foreground max-w-lg mb-8 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {slide.subtitle}
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row items-start gap-3 mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Button asChild size="lg" className="bg-gradient-primary text-primary-foreground px-8 h-14 text-base font-semibold shadow-glow hover:opacity-90 transition-all">
                <Link to={slide.cta_link || "/cadastro"}>
                  {slide.cta_text || config.hero_cta_text}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              {config.show_demo_link && (
                <Button asChild size="lg" variant="outline" className="h-14 px-6 text-base gap-2">
                  <Link to="/lojademo">
                    <Play className="h-4 w-4" />
                    Ver Demo
                  </Link>
                </Button>
              )}
            </motion.div>

            {/* Trust badges */}
            <motion.div
              className="flex items-center gap-6 flex-wrap"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              {[
                { icon: Shield, text: "Dados protegidos" },
                { icon: Clock, text: "Setup em 5 min" },
                { icon: Zap, text: "Sem taxa de adesão" },
              ].map((badge, i) => (
                <div key={i} className="flex items-center gap-2 text-muted-foreground text-sm">
                  <badge.icon className="h-4 w-4 text-primary" />
                  <span>{badge.text}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right column - Stats card */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <div className="rounded-3xl bg-card border border-border p-8 shadow-card relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-primary" />
              
              <div className="grid grid-cols-3 gap-6 mb-8">
                {[
                  { value: 2500, suffix: "+", label: "Restaurantes" },
                  { value: 5000, suffix: "+", label: "Pedidos/mês" },
                  { value: 99, suffix: ".9%", label: "Uptime" },
                ].map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-2xl md:text-3xl font-bold text-primary font-display">
                      <Counter target={stat.value} suffix={stat.suffix} />
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Mock dashboard preview */}
              <div className="rounded-2xl bg-muted/50 border border-border p-4 space-y-3">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                  <span className="text-xs font-medium text-muted-foreground">Dashboard ao vivo</span>
                </div>
                {[
                  { label: "Pedidos hoje", value: "47", change: "+12%" },
                  { label: "Faturamento", value: "R$ 3.240", change: "+8%" },
                  { label: "Ticket médio", value: "R$ 68,90", change: "+3%" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <span className="text-sm text-muted-foreground">{item.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{item.value}</span>
                      <span className="text-xs text-green-500 font-medium">{item.change}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Slide navigation */}
              {slides.length > 1 && (
                <div className="flex items-center justify-center gap-3 mt-6">
                  <button onClick={prev} className="p-1.5 rounded-full border border-border hover:bg-secondary transition-colors">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrent(i)}
                      className={`w-2 h-2 rounded-full transition-all ${i === current ? 'bg-primary scale-125' : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'}`}
                    />
                  ))}
                  <button onClick={next} className="p-1.5 rounded-full border border-border hover:bg-secondary transition-colors">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
